/**
 * Operator user detail page — a single account's IDENTITY (real: id, email
 * [masked + reveal], role, scope, status) plus an optional RECENT ACTIVITY feed
 * and MFA/last-seen rows that are flag-gated and clearly labelled SAMPLE (no
 * backend audit/MFA/last-seen field yet). Gated by `admin:cross_tenant_read`.
 *
 * NOTE: there is no `GET /operator/users/:id` yet, so the user is found in the
 * directory window (`useOperatorUsers({ limit: 200 })`). FOLLOW-UP: a real
 * single-user operator endpoint + a per-user audit feed.
 */

import { type OperatorUser, useOperatorUsers } from '@hostesa/api-client';
import { useFlag } from '@hostesa/feature-flags';
import { useT } from '@hostesa/i18n';
import { Button, Card, cn, ErrorState, LoadingState, type PillTone, StatusPill } from '@hostesa/ui';
import { type ReactNode, useState } from 'react';

const PAGE_WRAP = 'mx-auto w-full max-w-5xl px-4 py-6 sm:px-8 sm:py-8';
const EYEBROW =
  'mb-1 font-[var(--font-mono)] text-[10px] uppercase tracking-[0.14em] text-[var(--color-stone-mid)]';
const SECTION_EYEBROW =
  'mb-3 flex items-center gap-2.5 font-[var(--font-mono)] text-[10px] uppercase tracking-[0.14em] text-[var(--color-stone-mid)] before:h-px before:w-[18px] before:bg-[var(--color-line)]';
const SAMPLE_NOTE =
  'mb-2 font-[var(--font-mono)] text-[10px] uppercase tracking-[0.14em] text-[var(--color-stone-mid)]';

function statusTone(status: string): PillTone {
  if (status === 'active') return 'success';
  if (status === 'disabled') return 'alert';
  return 'accent';
}
function shortId(id: string): string {
  return `U-${id.slice(0, 8)}`;
}
function initialsOf(name: string | null, email: string): string {
  const src = (name ?? email).trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2 && parts[0] && parts[1]) {
    return (parts[0][0]! + parts[1][0]!).toUpperCase();
  }
  return src.slice(0, 2).toUpperCase();
}
/** Mask an email: keep the first char + domain, hide the rest. */
function maskEmail(email: string): string {
  const at = email.indexOf('@');
  if (at <= 0) return email;
  return `${email[0]}••••${email.slice(at)}`;
}

function DefRow({ label, value, mono }: { label: string; value: ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-t border-[var(--color-line-2)] py-2.5 text-[13.5px] first:border-t-0">
      <span className="text-[var(--color-stone-mid)]">{label}</span>
      <span
        className={cn(
          'text-right font-medium text-[var(--color-charcoal)]',
          mono && 'font-[var(--font-mono)] text-[12px] font-normal',
        )}
      >
        {value}
      </span>
    </div>
  );
}

export interface UserDetailPageProps {
  userId: string;
  /** Optional back handler (router wires navigation). */
  onBack?: () => void;
}

export function UserDetailPage({ userId, onBack }: UserDetailPageProps) {
  const { t } = useT('backoffice');
  const showSample = useFlag('backoffice_provider_diagnostics');
  const [revealed, setRevealed] = useState(false);

  // No GET /operator/users/:id yet — find the user in the directory window.
  const users = useOperatorUsers({ limit: 200 });

  if (users.isLoading) {
    return (
      <section data-testid="user-detail-page" className={PAGE_WRAP}>
        <LoadingState data-testid="user-detail-loading" title={t('oversight.loading')} />
      </section>
    );
  }
  if (users.isError) {
    return (
      <section data-testid="user-detail-page" className={PAGE_WRAP}>
        <ErrorState
          data-testid="user-detail-error"
          title={t('oversight.error.title')}
          description={t('oversight.error.description')}
        />
      </section>
    );
  }

  const user: OperatorUser | undefined = (users.data?.items ?? []).find((u) => u.userId === userId);

  if (!user) {
    return (
      <section data-testid="user-detail-page" className={PAGE_WRAP}>
        <ErrorState
          data-testid="user-detail-not-found"
          title={t('oversight.userDetailPage.notFound.title')}
          description={t('oversight.userDetailPage.notFound.description')}
        />
      </section>
    );
  }

  const name = user.fullName ?? user.email;
  const roleLabel = t(`oversight.users.roles.${user.role ?? 'none'}`);
  const scope =
    user.role === 'hostesa_admin'
      ? t('oversight.users.internalScope')
      : (user.tenantName ?? t('oversight.users.unknownScope'));

  // SAMPLE recent-activity feed (no operator audit endpoint yet).
  const sampleActivity = [
    { id: 'a1', at: '21:05:39', action: 'impersonation.start', ref: 'U-2241', outcome: 'success' },
    {
      id: 'a2',
      at: '19:58:41',
      action: 'call.recording.access',
      ref: 'C-7738',
      outcome: 'success',
    },
  ];

  return (
    <section data-testid="user-detail-page" className={PAGE_WRAP}>
      {/* Header --------------------------------------------------------------- */}
      <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className={EYEBROW}>
            {`${t('oversight.userDetailPage.eyebrow')} › ${t('oversight.userDetailPage.crumb')}`}
          </p>
          <h1 className="font-[var(--font-display)] text-2xl text-[var(--color-charcoal)]">
            {name}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex flex-col">
            <span className="font-[var(--font-mono)] text-[9px] uppercase tracking-[0.14em] text-[var(--color-stone-mid)]">
              {t('oversight.userDetailPage.meta.role')}
            </span>
            <span className="text-[12.5px] font-medium text-[var(--color-charcoal)]">
              {roleLabel}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="font-[var(--font-mono)] text-[9px] uppercase tracking-[0.14em] text-[var(--color-stone-mid)]">
              {t('oversight.userDetailPage.meta.id')}
            </span>
            <span className="font-[var(--font-mono)] text-[12.5px] text-[var(--color-charcoal)]">
              {shortId(user.userId)}
            </span>
          </div>
          {onBack && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="user-detail-back-btn"
              onClick={onBack}
            >
              {t('oversight.userDetailPage.backToUsers')}
            </Button>
          )}
        </div>
      </header>

      {/* Identity strip ------------------------------------------------------ */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <span
          aria-hidden="true"
          className="grid size-10 shrink-0 place-items-center rounded-full bg-[var(--color-canvas)] font-[var(--font-display)] text-sm font-semibold text-[var(--color-charcoal)]"
        >
          {initialsOf(user.fullName, user.email)}
        </span>
        <div className="flex flex-col">
          <span className="font-[var(--font-display)] text-lg text-[var(--color-charcoal)]">
            {name}
          </span>
          <span className="font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]">
            {`${roleLabel} · ${scope}`}
          </span>
        </div>
        <StatusPill
          dot
          tone={statusTone(user.status)}
          label={t(`oversight.users.status.${user.status}`)}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* IDENTITY (REAL) -------------------------------------------------- */}
        <div>
          <p className={SECTION_EYEBROW}>{t('oversight.userDetailPage.identity.title')}</p>
          <Card data-testid="user-detail-identity" className="p-4 sm:p-5">
            <DefRow
              label={t('oversight.userDetailPage.identity.userId')}
              value={shortId(user.userId)}
              mono
            />
            <DefRow
              label={t('oversight.userDetailPage.identity.email')}
              value={
                <span className="inline-flex items-center gap-2">
                  <span className="font-[var(--font-mono)] text-[12px] font-normal">
                    {revealed ? user.email : maskEmail(user.email)}
                  </span>
                  <button
                    type="button"
                    data-testid="user-detail-reveal"
                    onClick={() => setRevealed((r) => !r)}
                    className="font-medium text-[var(--color-charcoal)] underline-offset-2 hover:underline"
                  >
                    {revealed ? t('oversight.users.hide') : t('oversight.users.reveal')}
                  </button>
                </span>
              }
            />
            <DefRow label={t('oversight.userDetailPage.identity.role')} value={roleLabel} />
            <DefRow label={t('oversight.userDetailPage.identity.scope')} value={scope} />
            {showSample && (
              <>
                <DefRow
                  label={`${t('oversight.userDetailPage.identity.mfa')} · ${t('oversight.userDetailPage.sampleNote')}`}
                  value={
                    <StatusPill
                      dot
                      tone="success"
                      label={t('oversight.userDetailPage.identity.mfaEnabled')}
                    />
                  }
                />
                <DefRow
                  label={`${t('oversight.userDetailPage.identity.lastSeen')} · ${t('oversight.userDetailPage.sampleNote')}`}
                  value="2m ago"
                  mono
                />
              </>
            )}
          </Card>
          <div className="mt-3 flex items-center gap-4">
            <Button type="button" variant="ghost" size="sm" data-testid="user-detail-reset-mfa">
              {t('oversight.userDetailPage.actions.resetMfa')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="user-detail-suspend"
              className="text-[var(--color-alert)]"
            >
              {t('oversight.userDetailPage.actions.suspend')}
            </Button>
          </div>
        </div>

        {/* RECENT ACTIVITY (flag-gated SAMPLE) ------------------------------ */}
        {showSample && (
          <div>
            <p className={SECTION_EYEBROW}>{t('oversight.userDetailPage.activity.title')}</p>
            <Card data-testid="user-detail-activity" className="p-4 sm:p-5">
              <p className={SAMPLE_NOTE}>{t('oversight.userDetailPage.sampleNote')}</p>
              <ul className="flex flex-col">
                {sampleActivity.map((ev) => (
                  <li
                    key={ev.id}
                    className="flex items-center gap-4 border-t border-[var(--color-line-2)] py-2.5 first:border-t-0"
                  >
                    <span className="w-16 shrink-0 font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]">
                      {ev.at}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-[var(--font-mono)] text-[12.5px] text-[var(--color-charcoal)]">
                        {ev.action}
                      </p>
                      <p className="font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]">
                        {ev.ref}
                      </p>
                    </div>
                    <StatusPill tone="neutral" label={ev.outcome} />
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        )}
      </div>
    </section>
  );
}
